import { useDispatch, useSelector } from 'react-redux';
import useFetchConfig from './useFetchConfig';
import useInteractions from './useInteractions';

import { updateRecordInCollection } from 'lib/helpers';
import { selectCurrentUser, spendCredit } from 'features/auth/slice';
import { updateScreenshot } from 'features/solutions/slice';

import {
  recordsContain,
  recordsMatch,
} from '@frontendmentorio/design-system-v2';

export default function useSolutions() {
  const currentUser = useSelector(selectCurrentUser);
  const disptach = useDispatch();

  const { removeInteractions, setInteractions } = useInteractions();
  const { mutate, mutateAll, mutateInfinite } = useFetchConfig();

  const mutateCache = async (updateFn, { challenge, user, ...solution }) => {
    const challengeId = challenge.id ?? challenge;

    const keys = [
      '/api/v3/learner/feed',
      '/api/v3/learner/solutions',
      '/api/v3/comments',
      '/api/v3/solutions',
      '/api/v3/solutions?filter=all',
      `/api/v3/solutions?filter=challenge|${challengeId}`,
      `/api/v3/learner/solutions?filter=challenge|${challengeId}`,
    ];

    const updater = async ({ data, ...rest } = {}) => ({
      data: updateFn(data, solution),
      ...rest,
    });

    await mutate(
      `/api/v3/solutions/${solution.id}`,
      async ({ data }) => ({ data: { ...data, ...solution } }),
      { revalidate: false }
    );

    for (const key of keys) {
      await mutateInfinite(key, updater, { revalidate: false });
    }
  };

  const removeSolution = (collection, solution) => {
    return collection.filter((item) => {
      if (item.solution) return !recordsMatch(item.solution, solution);
      return !recordsMatch(item, solution);
    });
  };

  const updateSolution = (collection, solution) => {
    return updateRecordInCollection(
      collection,
      (item) => recordsMatch(item.solution || item, solution),
      (item) => {
        if (!item.solution) return { ...item, ...solution };
        return { ...item, solution: { ...item.solution, ...solution } };
      }
    );
  };

  const bookmark = async (solution) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}/bookmark`, {
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    setInteractions(solution, { bookmarks: body.data.bookmarks });

    if (recordsContain(body.data.bookmarks, currentUser.id)) {
      return await mutateAll('/api/v3/learner/bookmarks');
    }

    await mutateInfinite(
      '/api/v3/learner/bookmarks',
      async ({ data, ...rest }) => ({
        data: data.filter((solution) => !recordsMatch(solution, body.data)),
        ...rest,
      }),
      { revalidate: false }
    );
  };

  const create = async (formData) => {
    const keys = [
      '/api/v3/learner/solutions',
      `/api/v3/learner/solutions?filter=challenge|${formData.challenge}`,
      '/api/v3/solutions',
      '/api/v3/solutions?filter=all',
    ];

    const res = await fetch('/api/v3/solutions', {
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    const updater = async ([first, ...rest]) => {
      if (!first) return [{ data: [body.data], meta: {} }];

      return [
        { data: [body.data].concat(first.data), meta: first.meta },
        ...rest,
      ];
    };

    for (const key of keys) {
      await mutate(`$inf$${key}`, updater, { revalidate: false });
    }

    await mutateAll('/api/v3/wall-of-fame');
    await mutate('/api/v3/learner/challenges/in-progress');
    await mutate('/api/v3/learner/challenges/completed');

    return body.data;
  };

  const like = async (solution) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}/like`, {
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    setInteractions(solution, { likes: body.data.likes });
  };

  const remove = async (solution) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message);
    }

    await mutateCache(removeSolution, solution);

    await mutateAll('/api/v3/wall-of-fame');
    await mutateAll('/api/v3/learner/challenges/in-progress');
    await mutateAll('/api/v3/learner/challenges/completed');

    removeInteractions(solution);
  };

  const screenshot = async (solution) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}/screenshot`, {
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    await disptach(spendCredit('screenshots'));
    await disptach(updateScreenshot(body.data));

    await mutateCache(updateSolution, {
      id: solution.id,
      challenge: solution.challenge,
      screenshot: body.data.screenshot,
    });
  };

  const update = async (solution, formData) => {
    const res = await fetch(`/api/v3/solutions/${solution.id}`, {
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
      method: 'PUT',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    await mutateCache(updateSolution, body.data);
  };

  return { bookmark, create, like, remove, screenshot, update };
}
